import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    PhoneCall,
    Smile,
    Stethoscope,
    Building2,
    Database,
    ShieldCheck,
    LineChart,
    ArrowUpRight,
    X,
    CheckCircle2,
    Clock,
    Users,
    Layers,
} from "lucide-react";

export const SERVICES = [
    {
        icon: PhoneCall,
        title: "CATI Surveys",
        desc: "Computer-assisted telephone interviews run on enterprise-grade infrastructure with live monitoring.",
        accent: "blue",
        details: {
            tagline: "Voice-led research, instrumented end-to-end.",
            paragraph:
                "Our CATI rooms blend trained interviewers with smart dialer software, so every conversation is recorded, scored, and routed to the right segment in real time. You see progress on a live dashboard — completes, refusals, quotas — instead of waiting for a weekly status email.",
            features: [
                "Multi-language interviewers with live supervisor consoles",
                "Smart skip logic, quotas and dial-rule automation",
                "Voice-audited QC on every completed interview",
                "Daily progress dashboards + downloadable raw data",
            ],
            stats: [
                { label: "Avg. response rate", value: "47%" },
                { label: "Avg. survey length", value: "8.4 min" },
                { label: "Languages supported", value: "20+" },
            ],
            useCases: [
                "B2B decision-maker interviews",
                "Healthcare HCP / patient programs",
                "Brand & comms tracking studies",
            ],
        },
    },
    {
        icon: Smile,
        title: "Customer Satisfaction",
        desc: "NPS, CSAT and brand health programs that turn voice-of-customer signals into KPIs.",
        accent: "green",
        details: {
            tagline: "Make voice-of-customer the operating signal of your business.",
            paragraph:
                "We design longitudinal CX programs (NPS, CSAT, CES) with closed-loop alerting, so detractors get routed to the right team within hours — not next quarter.",
            features: [
                "Transactional + relational NPS programs",
                "Driver analysis and key-driver heatmaps",
                "Closed-loop alerts to ops, retail or sales",
                "Executive scorecards, monthly delivered",
            ],
            stats: [
                { label: "Closed-loop SLA", value: "< 24h" },
                { label: "CSAT lift seen", value: "+22%" },
                { label: "Programs run", value: "60+" },
            ],
            useCases: [
                "Retail / e-commerce post-purchase",
                "B2B account health tracking",
                "Telecom & financial services CX",
            ],
        },
    },
    {
        icon: Stethoscope,
        title: "Healthcare Research",
        desc: "Patient experience, HCP studies, and access research with strict privacy controls.",
        accent: "blue",
        details: {
            tagline: "Healthcare-grade insight without the compliance headaches.",
            paragraph:
                "From patient adherence pulses to HCP and KOL interviews, we operate under documented privacy controls, role-based access, and trained healthcare-specialist interviewers.",
            features: [
                "Patient experience & adherence studies",
                "HCP / specialist / KOL interviews",
                "Therapy area perceptions and access research",
                "HIPAA-aware data handling and encrypted storage",
            ],
            stats: [
                { label: "Validation pass rate", value: "99.2%" },
                { label: "Therapy areas", value: "30+" },
                { label: "HCP panel reach", value: "12 countries" },
            ],
            useCases: [
                "New therapy launch readiness",
                "Adherence & switching studies",
                "Patient journey research",
            ],
        },
    },
    {
        icon: Building2,
        title: "B2B / B2C Research",
        desc: "From decision-maker interviews to consumer panels — segmented, profiled, validated.",
        accent: "green",
        details: {
            tagline: "Reach the people who actually move the metric.",
            paragraph:
                "We build the audience for you — verified job titles for B2B, profiled and weighted samples for B2C — and field across CATI, online or hybrid modes for the right cost-quality balance.",
            features: [
                "Verified decision-maker outreach (CXO, IT, HR, etc.)",
                "Profile validation, screeners and quotas",
                "Cross-market comparable research",
                "Hybrid CATI + online panel methodology",
            ],
            stats: [
                { label: "B2B titles verified", value: "100%" },
                { label: "Avg. fielding time", value: "10 days" },
                { label: "Markets fielded", value: "12+" },
            ],
            useCases: [
                "ICP and pricing discovery",
                "Concept tests & feature prioritisation",
                "Brand tracker — multi-market",
            ],
        },
    },
    {
        icon: Database,
        title: "Data Collection",
        desc: "Multi-mode capture: CATI, online and hybrid. Custom screeners, quotas and routing.",
        accent: "blue",
        details: {
            tagline: "Mode-agnostic fieldwork, designed around your audience.",
            paragraph:
                "Sometimes the answer is a 9-minute call. Sometimes it's a 6-minute online survey. We design the right mode mix, not a one-size methodology.",
            features: [
                "CATI, online and hybrid data capture",
                "Smart routing and screener logic",
                "Soft and hard quotas with live monitoring",
                "Multi-region scaling without quality drift",
            ],
            stats: [
                { label: "Capacity", value: "200k+ interviews/yr" },
                { label: "Modes supported", value: "3" },
                { label: "Real-time dashboards", value: "Yes" },
            ],
            useCases: [
                "Large-scale tracker programs",
                "Hard-to-reach B2B segments",
                "Multi-country syndicated studies",
            ],
        },
    },
    {
        icon: ShieldCheck,
        title: "Data Validation",
        desc: "Multi-layer QC: voice audits, logic checks, fraud scoring and human verification.",
        accent: "green",
        details: {
            tagline: "Defensible data — by design, not by audit.",
            paragraph:
                "Every record is routed through automated logic checks, fraud-scoring heuristics and a human verification layer. You can trust the rows you're putting into a model or a board deck.",
            features: [
                "Voice audits on a sampled basis",
                "Logic, range and consistency checks",
                "Fraud scoring + duplicate detection",
                "Independent human verification layer",
            ],
            stats: [
                { label: "Validation pass rate", value: "99.2%" },
                { label: "Fraud caught", value: "< 0.4%" },
                { label: "QC layers", value: "4" },
            ],
            useCases: [
                "Pre-launch readiness datasets",
                "Regulatory or audited research",
                "Data feeding into pricing models",
            ],
        },
    },
    {
        icon: LineChart,
        title: "Reporting & Insights",
        desc: "Dashboards, executive narratives, and recommendations — not just charts.",
        accent: "blue",
        details: {
            tagline: "We don't deliver charts. We deliver the next decision.",
            paragraph:
                "Cross-tabs and topline decks are table stakes. We add a story-led executive narrative and a prioritised list of what to do — written for the people who actually have to act.",
            features: [
                "Topline + detailed cross-tab decks",
                "Live web + mobile dashboards",
                "Story-led recommendations, ranked",
                "Custom segmentation and drivers",
            ],
            stats: [
                { label: "Dashboards delivered", value: "120+" },
                { label: "Avg. time to insight", value: "7 days" },
                { label: "Exec satisfaction", value: "92%" },
            ],
            useCases: [
                "Quarterly brand & CX reviews",
                "Single-decision research",
                "Strategy and pricing committees",
            ],
        },
    },
];

function ServiceCard({ service, index, onOpen }) {
    const Icon = service.icon;
    const isBlue = service.accent === "blue";
    return (
        <motion.button
            type="button"
            onClick={() => onOpen(service)}
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: index * 0.05 }}
            className="pr-card rounded-2xl p-6 relative group overflow-hidden text-left w-full focus:outline-none focus:ring-2 focus:ring-blue-400/50"
            data-testid={`service-card-${index}`}
            aria-label={`Learn more about ${service.title}`}
        >
            <div
                className={`absolute -top-12 -right-10 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 ${
                    isBlue ? "bg-blue-500/40" : "bg-emerald-500/40"
                }`}
            />
            <div
                className={`relative w-12 h-12 rounded-xl flex items-center justify-center ${
                    isBlue
                        ? "bg-blue-500/10 border border-blue-400/30"
                        : "bg-emerald-500/10 border border-emerald-400/30"
                }`}
            >
                <Icon
                    className={`w-5 h-5 ${
                        isBlue ? "text-blue-300" : "text-emerald-300"
                    } group-hover:scale-110 transition-transform duration-300`}
                />
            </div>
            <h3 className="mt-5 font-outfit text-xl font-semibold text-white">
                {service.title}
            </h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                {service.desc}
            </p>
            <div className="mt-5 flex items-center gap-2 text-[11px] font-mono-pr uppercase tracking-[0.2em] text-blue-300/80 group-hover:text-blue-300">
                Learn more
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
        </motion.button>
    );
}

function ServiceDialog({ service, onClose }) {
    if (!service) return null;
    const Icon = service.icon;
    const isBlue = service.accent === "blue";
    const d = service.details;

    return (
        <AnimatePresence>
            <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[100] bg-[#020611]/80 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={onClose}
                data-testid="service-dialog-overlay"
            >
                <motion.div
                    key="content"
                    initial={{ opacity: 0, y: 24, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 16, scale: 0.97 }}
                    transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="pr-glass-strong rounded-3xl w-full max-w-2xl max-h-[88vh] overflow-y-auto relative"
                    data-testid="service-dialog"
                >
                    <div className="absolute -top-24 -right-20 w-72 h-72 bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 w-9 h-9 rounded-xl pr-glass flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition"
                        aria-label="Close"
                        data-testid="service-dialog-close"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="relative p-7 sm:p-9">
                        <div
                            className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                                isBlue
                                    ? "bg-blue-500/10 border border-blue-400/30"
                                    : "bg-emerald-500/10 border border-emerald-400/30"
                            }`}
                        >
                            <Icon
                                className={`w-6 h-6 ${
                                    isBlue
                                        ? "text-blue-300"
                                        : "text-emerald-300"
                                }`}
                            />
                        </div>

                        <h3 className="mt-5 font-outfit text-2xl sm:text-3xl font-bold text-white tracking-tight">
                            {service.title}
                        </h3>
                        <p
                            className={`mt-2 font-outfit text-sm ${
                                isBlue ? "text-blue-300" : "text-emerald-300"
                            }`}
                        >
                            {d.tagline}
                        </p>
                        <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
                            {d.paragraph}
                        </p>

                        {/* Stats */}
                        <div className="mt-6 grid grid-cols-3 gap-2.5">
                            {d.stats.map((s) => (
                                <div
                                    key={s.label}
                                    className="pr-glass rounded-xl px-3 py-3 text-center"
                                >
                                    <p className="font-outfit text-base sm:text-lg font-bold text-white">
                                        {s.value}
                                    </p>
                                    <p className="mt-0.5 font-mono-pr text-[10px] uppercase tracking-widest text-slate-400">
                                        {s.label}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Features */}
                        <div className="mt-7">
                            <p className="font-outfit text-xs uppercase tracking-[0.25em] text-blue-400 font-bold flex items-center gap-2">
                                <Layers className="w-3.5 h-3.5" />
                                What's included
                            </p>
                            <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
                                {d.features.map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-2.5 text-sm text-slate-300"
                                    >
                                        <CheckCircle2
                                            className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                                isBlue
                                                    ? "text-blue-300"
                                                    : "text-emerald-300"
                                            }`}
                                        />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Use cases */}
                        <div className="mt-6">
                            <p className="font-outfit text-xs uppercase tracking-[0.25em] text-emerald-300 font-bold flex items-center gap-2">
                                <Users className="w-3.5 h-3.5" />
                                Common use cases
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {d.useCases.map((u) => (
                                    <span
                                        key={u}
                                        className="text-xs font-outfit px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-slate-300"
                                    >
                                        {u}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <a
                                href="#lead-form"
                                onClick={onClose}
                                className="btn-primary justify-center flex-1"
                                data-testid="service-dialog-cta"
                            >
                                Run a {service.title.toLowerCase()} study
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <div className="flex items-center justify-center gap-2 text-xs font-mono-pr text-slate-400 px-4">
                                <Clock className="w-3.5 h-3.5 text-blue-300" />
                                Reply within 1 business day
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export default function Services() {
    const [active, setActive] = useState(null);

    return (
        <section
            id="services"
            className="relative py-28"
            data-testid="services-section"
        >
            <div className="pr-container">
                <SectionHeader
                    overline="What we do"
                    title="Services that turn conversations into clarity"
                    subtitle="Seven precision-built capabilities — composable into any market research project."
                />

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {SERVICES.map((s, i) => (
                        <ServiceCard
                            key={s.title}
                            service={s}
                            index={i}
                            onOpen={setActive}
                        />
                    ))}
                </div>
            </div>

            {active && (
                <ServiceDialog
                    service={active}
                    onClose={() => setActive(null)}
                />
            )}
        </section>
    );
}

export function SectionHeader({ overline, title, subtitle, align = "left" }) {
    return (
        <div
            className={`max-w-3xl ${
                align === "center" ? "mx-auto text-center" : ""
            }`}
        >
            <motion.span
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="pr-chip"
            >
                {overline}
            </motion.span>
            <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-4 font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white"
            >
                {title}
            </motion.h2>
            {subtitle && (
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="mt-4 text-slate-400 leading-relaxed"
                >
                    {subtitle}
                </motion.p>
            )}
        </div>
    );
}
